import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Tooltip,
  Alert,
  useTheme,
  useMediaQuery,
  Divider
} from '@mui/material';
import {
  Gavel,
  CheckCircle,
  Cancel,
  Visibility,
  Warning,
  Search,
  FilterList,
  Edit
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const P2POrderManagement = ({ onUpdate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [interventionDialogOpen, setInterventionDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [interventionData, setInterventionData] = useState({
    resolution: '',
    admin_notes: ''
  });
  const [commissionDialogOpen, setCommissionDialogOpen] = useState(false);
  const [commissionData, setCommissionData] = useState({
    commission_amount: '',
    commission_percentage: ''
  });
  const [filters, setFilters] = useState({
    status: '',
    partner_id: '',
    date_from: '',
    date_to: ''
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  });
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, [filters, pagination.current_page]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const { default: apiClient } = await import('../../request/http-common');
      
      const params = new URLSearchParams({
        page: pagination.current_page,
        per_page: pagination.per_page,
        ...filters
      });

      const response = await apiClient.get(`/api/admin/p2p/orders?${params}`);
      
      if (response.data && response.data.success && response.data.orders) {
        setOrders(response.data.orders.data || []);
        setSummary(response.data.summary || {});
        setPagination({
          current_page: response.data.orders.current_page || 1,
          per_page: response.data.orders.per_page || 15,
          total: response.data.orders.total || 0,
          last_page: response.data.orders.last_page || 1
        });
      } else {
        setOrders([]);
        setSummary({});
        setPagination({
          current_page: 1,
          per_page: 15,
          total: 0,
          last_page: 1
        });
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleIntervention = (order) => {
    setSelectedOrder(order);
    setInterventionData({ resolution: '', admin_notes: '' });
    setInterventionDialogOpen(true);
  };

  const executeIntervention = async () => {
    try {
      const { default: apiClient } = await import('../../request/http-common');
      const response = await apiClient.post(`/api/admin/p2p/orders/${selectedOrder.id}/intervene`, interventionData);
      
      if (response.data.success) {
        fetchOrders();
        onUpdate && onUpdate();
        setInterventionDialogOpen(false);
      }
    } catch (error) {
      console.error('Error executing intervention:', error);
    }
  };

  const handleCommissionEdit = (order) => {
    setSelectedOrder(order);
    setCommissionData({
      commission_amount: order.commission_amount || '',
      commission_percentage: order.commission_percentage || ''
    });
    setCommissionDialogOpen(true);
  };

  const updateCommission = async () => {
    try {
      const { default: apiClient } = await import('../../request/http-common');
      const response = await apiClient.put(`/api/admin/p2p/orders/${selectedOrder.id}/commission`, commissionData);
      
      if (response.data.success) {
        fetchOrders();
        onUpdate && onUpdate();
        setCommissionDialogOpen(false);
      }
    } catch (error) {
      console.error('Error updating commission:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handlePageChange = (event, page) => {
    setPagination(prev => ({ ...prev, current_page: page }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      case 'disputed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'accepted':
        return 'Accepted';
      case 'payment_made':
        return 'Payment Made';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      case 'disputed':
        return 'Disputed';
      default:
        return status;
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box>
      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {summary.total_orders}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Orders
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="error.main" fontWeight="bold">
                  {summary.disputed_orders}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Disputed Orders
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {formatCurrency(summary.total_volume)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Volume
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="warning.main" fontWeight="bold">
                  {summary.today_orders}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Today's Orders
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filter Orders
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="accepted">Accepted</MenuItem>
                  <MenuItem value="payment_made">Payment Made</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="disputed">Disputed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From Date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To Date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order ID</TableCell>
              <TableCell>Partner</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Pair</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Commission</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => (
              <TableRow key={order.id}>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {order.order_id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2">
                      {order.partner?.partner_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {order.partner?.user?.email}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2">
                      {order.customer?.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {order.customer?.email}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={`${order.cryptocurrency}/${order.fiat_currency}`}
                    variant="outlined"
                    size="small"
                  />
                  <Typography variant="caption" display="block" color="text.secondary">
                    {order.trade_type.toUpperCase()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {order.crypto_amount} {order.cryptocurrency}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatCurrency(order.fiat_amount, order.fiat_currency)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(order.commission_amount || 0, order.fiat_currency)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(order.commission_percentage || 0)}%
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusText(order.status)}
                    color={getStatusColor(order.status)}
                    size="small"
                  />
                  {order.status === 'disputed' && (
                    <Warning sx={{ color: 'warning.main', fontSize: 16, ml: 1 }} />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {formatDate(order.created_at)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Edit Commission">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleCommissionEdit(order)}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    {order.status === 'disputed' && (
                      <Tooltip title="Intervene">
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => handleIntervention(order)}
                        >
                          <Gavel />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {pagination.last_page > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pagination.last_page}
            page={pagination.current_page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {/* Intervention Dialog */}
      <Dialog
        open={interventionDialogOpen}
        onClose={() => setInterventionDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Order Intervention</DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                You are intervening in a disputed order. This action will override the normal P2P process.
              </Alert>
              
              <Typography variant="h6" gutterBottom>
                Order Details
              </Typography>
              <Typography variant="body2" gutterBottom>
                Order ID: {selectedOrder.order_id}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Partner: {selectedOrder.partner?.partner_name}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Amount: {selectedOrder.crypto_amount} {selectedOrder.cryptocurrency} 
                ({formatCurrency(selectedOrder.fiat_amount, selectedOrder.fiat_currency)})
              </Typography>
              
              {selectedOrder.dispute_reason && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Dispute Reason:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedOrder.dispute_reason}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Resolution</InputLabel>
                <Select
                  value={interventionData.resolution}
                  onChange={(e) => setInterventionData(prev => ({ ...prev, resolution: e.target.value }))}
                  label="Resolution"
                  required
                >
                  <MenuItem value="complete">Complete Order (Release funds to customer)</MenuItem>
                  <MenuItem value="cancel">Cancel Order (Refund to partner)</MenuItem>
                  <MenuItem value="refund">Partial Refund</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Admin Notes"
                multiline
                rows={4}
                value={interventionData.admin_notes}
                onChange={(e) => setInterventionData(prev => ({ ...prev, admin_notes: e.target.value }))}
                helperText="Explain the reason for this intervention and any actions taken"
                required
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInterventionDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={executeIntervention}
            color="warning"
            variant="contained"
            disabled={!interventionData.resolution || !interventionData.admin_notes}
          >
            Execute Intervention
          </Button>
        </DialogActions>
      </Dialog>

      {/* Commission Dialog */}
      <Dialog
        open={commissionDialogOpen}
        onClose={() => setCommissionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Commission</DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Order: {selectedOrder.order_id}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Partner: {selectedOrder.partner?.partner_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Amount: {selectedOrder.crypto_amount} {selectedOrder.cryptocurrency} 
                ({formatCurrency(selectedOrder.fiat_amount, selectedOrder.fiat_currency)})
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Commission Amount"
                    type="number"
                    value={commissionData.commission_amount}
                    onChange={(e) => setCommissionData(prev => ({ 
                      ...prev, 
                      commission_amount: e.target.value 
                    }))}
                    InputProps={{
                      startAdornment: (
                        <Typography sx={{ mr: 1 }}>
                          {selectedOrder.fiat_currency}
                        </Typography>
                      )
                    }}
                    inputProps={{ step: '0.01', min: '0' }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Commission Percentage"
                    type="number"
                    value={commissionData.commission_percentage}
                    onChange={(e) => setCommissionData(prev => ({ 
                      ...prev, 
                      commission_percentage: e.target.value 
                    }))}
                    InputProps={{
                      endAdornment: (
                        <Typography sx={{ ml: 1 }}>%</Typography>
                      )
                    }}
                    inputProps={{ step: '0.01', min: '0', max: '100' }}
                  />
                </Grid>
              </Grid>
              
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> Commission will be credited to the P2P partner's account. 
                  This action will be logged with your admin credentials.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommissionDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={updateCommission}
            disabled={!commissionData.commission_amount && !commissionData.commission_percentage}
          >
            Update Commission
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default P2POrderManagement;
